import { useState, useEffect, useCallback } from "react";
import { Trophy } from "lucide-react";
import MatchCard from "@/components/MatchCard";
import { Button } from "@/components/ui/button";
import api from "@/services/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { haversineKm, loadCachedPosition } from "@/services/geo";

export default function MatchesPage() {
  const { user } = useAuth();
  const { search_radius_km } = useSettings();
  const radiusKm = search_radius_km ?? 20;
  const [matches, setMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const position = loadCachedPosition();

  const loadMatches = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await api.getMatches();
      setMatches(data);
    } catch (error) {
      toast.error("Erreur lors du chargement des matchs");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  const filteredMatches = matches.filter((match) => {
    if (filter === "open") return match.status === "open";
    if (filter === "completed") {
      // "Historique" = matches that are completed AND the current user was a player
      if (match.status !== "completed") return false;
      if (!user?.id) return false;
      return (match.players || []).includes(user.id);
    }
    // "Tous": apply the configured radius when we have a GPS fix AND the match has coords.
    if (position?.lat != null && match.coordinates && Array.isArray(match.coordinates)) {
      const [mLng, mLat] = match.coordinates;
      const d = haversineKm(position.lat, position.lng, mLat, mLng);
      if (d > radiusKm) return false;
    }
    return true;
  });

  return (
    <div data-testid="matches-page" style={{ minHeight: '100vh', backgroundColor: '#000' }}>
      <header style={{ paddingTop: '32px', paddingBottom: '16px', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '4px' }}>
          <Trophy size={22} color="#22c55e" />
          <h1 className="font-sport uppercase" style={{ fontSize: '22px', fontWeight: 700, color: '#fff' }}>
            Matchs
          </h1>
        </div>
        <p style={{ color: '#6b7280', fontSize: '12px' }}>
          {filter === "all" && position?.lat != null
            ? `Matchs dans un rayon de ${radiusKm} km`
            : filter === "completed"
              ? "Tes matchs passes"
              : "Tous les matchs disponibles"}
        </p>
      </header>

      <section style={{ padding: '0 16px 16px 16px' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Button
            data-testid="filter-all"
            onClick={() => setFilter("all")}
            style={{
              borderRadius: '9999px', padding: '0 16px', height: '32px',
              fontSize: '12px', fontWeight: 500,
              backgroundColor: filter === "all" ? '#fff' : 'transparent',
              color: filter === "all" ? '#000' : '#9ca3af',
              border: filter === "all" ? 'none' : '1px solid #333',
            }}
          >
            Tous
          </Button>
          <Button
            data-testid="filter-open"
            onClick={() => setFilter("open")}
            style={{
              borderRadius: '9999px', padding: '0 16px', height: '32px',
              fontSize: '12px', fontWeight: 500,
              backgroundColor: filter === "open" ? '#22c55e' : 'transparent',
              color: filter === "open" ? '#fff' : '#9ca3af',
              border: filter === "open" ? 'none' : '1px solid #333',
            }}
          >
            Ouverts
          </Button>
          <Button
            data-testid="filter-completed"
            onClick={() => setFilter("completed")}
            style={{
              borderRadius: '9999px', padding: '0 16px', height: '32px',
              fontSize: '12px', fontWeight: 500,
              backgroundColor: filter === "completed" ? '#6b7280' : 'transparent',
              color: filter === "completed" ? '#fff' : '#9ca3af',
              border: filter === "completed" ? 'none' : '1px solid #333',
            }}
          >
            Historique
          </Button>
        </div>
      </section>

      <section style={{ padding: '0 16px 24px 16px' }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div className="animate-spin" style={{ width: '32px', height: '32px', border: '3px solid #333', borderTopColor: '#22c55e', borderRadius: '50%', margin: '0 auto' }} />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {filteredMatches.length > 0 ? (
              filteredMatches.map((match) => (
                <MatchCard
                  key={match.id}
                  match={match}
                  onDeleted={(id) => setMatches((prev) => prev.filter((m) => m.id !== id))}
                  onDeleteError={loadMatches}
                />
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>📭</div>
                <p style={{ color: '#9ca3af', fontSize: '14px' }}>
                  {filter === "completed"
                    ? "Tu n'as joue aucun match pour le moment"
                    : "Aucun match dans cette categorie"}
                </p>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
